import React, { useState, useEffect } from 'react'
import {
  Container,
  Grid,
  Header,
  Segment,
  Card,
  Icon,
  Button,
  Dimmer,
  Loader,
  Message,
  Tab,
} from 'semantic-ui-react'
import { Link } from 'react-router-dom'
import { fetchIndustries } from '../../../lib/datocms'
import './Industries.css'

const Industries = () => {
  const [industries, setIndustries] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    loadIndustries()
  }, [])

  const loadIndustries = async () => {
    try {
      setLoading(true)
      const data = await fetchIndustries()
      setIndustries(data)
    } catch (err) {
      console.error('Error loading industries:', err)
      setError('Failed to load industries. Please try again later.')
    } finally {
      setLoading(false)
    }
  }

  const renderIndustryCard = (industry) => {
    return (
      <Card key={industry.id} as={Link} to={`/industries/${industry.slug}`} className="industry-card">
        <Card.Content>
          {industry.icon?.url && (
            <div className="industry-icon">
              <img src={industry.icon.url} alt={industry.icon.alt || industry.name} />
            </div>
          )}
          <Card.Header>{industry.name}</Card.Header>
          <Card.Description>{industry.shortDescription}</Card.Description>
        </Card.Content>
        <Card.Content extra>
          <Button primary fluid>
            Explore <Icon name='arrow right' />
          </Button>
        </Card.Content>
      </Card>
    )
  }

  const industryPanes = [
    {
      menuItem: 'Construction & Building',
      render: () => (
        <Tab.Pane>
          <Grid columns={2} stackable>
            <Grid.Column>
              <Header as='h3'>
                <Icon name='building' />
                Construction Materials
              </Header>
              <p>
                Complete solutions for construction material suppliers, contractors, 
                and builders. Manage lumber, steel, concrete, and specialty materials 
                with ease.
              </p>
              <Button as={Link} to='/industries/construction' primary>
                Learn More
              </Button>
            </Grid.Column>
            <Grid.Column>
              <Segment placeholder>
                <Header icon>
                  <Icon name='image' />
                  Construction Industry Image
                </Header>
              </Segment>
            </Grid.Column>
          </Grid>
        </Tab.Pane>
      ),
    },
    {
      menuItem: 'Manufacturing',
      render: () => (
        <Tab.Pane>
          <Grid columns={2} stackable>
            <Grid.Column>
              <Header as='h3'>
                <Icon name='industry' />
                Manufacturing & Production
              </Header>
              <p>
                Streamline your manufacturing supply chain with integrated procurement, 
                inventory management, and distribution tools designed for manufacturers.
              </p>
              <Button as={Link} to='/industries/manufacturing' primary>
                Learn More
              </Button>
            </Grid.Column>
            <Grid.Column>
              <Segment placeholder>
                <Header icon>
                  <Icon name='image' />
                  Manufacturing Industry Image
                </Header>
              </Segment>
            </Grid.Column>
          </Grid>
        </Tab.Pane>
      ),
    },
    {
      menuItem: 'Agriculture',
      render: () => (
        <Tab.Pane>
          <Grid columns={2} stackable>
            <Grid.Column>
              <Header as='h3'>
                <Icon name='leaf' />
                Agriculture & Farming
              </Header>
              <p>
                Connect farmers, distributors, and retailers with comprehensive 
                agricultural commodity trading and logistics management solutions.
              </p>
              <Button as={Link} to='/industries/agriculture' primary>
                Learn More
              </Button>
            </Grid.Column>
            <Grid.Column>
              <Segment placeholder>
                <Header icon>
                  <Icon name='image' />
                  Agriculture Industry Image
                </Header>
              </Segment>
            </Grid.Column>
          </Grid>
        </Tab.Pane>
      ),
    },
  ]

  if (loading) {
    return (
      <Dimmer active inverted>
        <Loader size='large'>Loading Industries...</Loader>
      </Dimmer>
    )
  }

  if (error) {
    return (
      <Container style={{ marginTop: '2em' }}>
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      </Container>
    )
  }

  return (
    <div className="industries-page">
      {/* Hero Section */}
      <Segment className="industries-hero" vertical>
        <Container text textAlign='center'>
          <Header as='h1' inverted>
            Solutions for Every Industry
          </Header>
          <p className="hero-subtitle">
            Trading Systems provides specialized solutions tailored to the unique 
            challenges and requirements of various industries. From construction to 
            manufacturing, we have the expertise and tools to transform your business.
          </p>
          <Button primary size='large' as={Link} to='/contact-us'>
            Find Your Industry Solution
          </Button>
        </Container>
      </Segment>

      {/* Industry Overview Tabs */}
      <Segment className="industry-tabs" vertical>
        <Container>
          <Header as='h2' textAlign='center' style={{ marginBottom: '2em' }}>
            Featured Industries
          </Header>
          <Tab panes={industryPanes} />
        </Container>
      </Segment>

      {/* Industries Grid from Dato CMS */}
      {industries.length > 0 && (
        <Segment className="industries-grid" vertical>
          <Container>
            <Header as='h2' textAlign='center' style={{ marginBottom: '2em' }}>
              All Industries We Serve
            </Header>
            <Card.Group itemsPerRow={4} stackable>
              {industries.map(industry => renderIndustryCard(industry))}
            </Card.Group>
          </Container>
        </Segment>
      )}

      {/* Industry Benefits */}
      <Segment className="industry-benefits" vertical>
        <Container>
          <Header as='h2' textAlign='center' style={{ marginBottom: '2em' }}>
            Industry-Specific Advantages
          </Header>
          
          <Grid columns={3} stackable>
            <Grid.Column>
              <div className="benefit-card">
                <Icon name='cogs' size='huge' color='blue' />
                <Header as='h3'>Customized Workflows</Header>
                <p>
                  Pre-configured workflows designed specifically for your industry's 
                  unique processes and requirements.
                </p>
              </div>
            </Grid.Column>
            <Grid.Column>
              <div className="benefit-card">
                <Icon name='shield' size='huge' color='green' />
                <Header as='h3'>Compliance Ready</Header>
                <p>
                  Built-in compliance features for industry-specific regulations 
                  and standards, keeping you audit-ready.
                </p>
              </div>
            </Grid.Column>
            <Grid.Column>
              <div className="benefit-card">
                <Icon name='users' size='huge' color='orange' />
                <Header as='h3'>Industry Network</Header>
                <p>
                  Connect with verified suppliers and buyers within your industry 
                  through our trusted marketplace network.
                </p>
              </div>
            </Grid.Column>
          </Grid>
        </Container>
      </Segment>

      {/* Success Metrics */}
      <Segment className="success-metrics" vertical>
        <Container>
          <Header as='h2' textAlign='center' style={{ marginBottom: '3em' }}>
            Proven Success Across Industries
          </Header>
          
          <Grid columns={4} stackable textAlign='center'>
            <Grid.Column>
              <div className="metric">
                <h2 className="metric-value">500+</h2>
                <p className="metric-label">Companies</p>
              </div>
            </Grid.Column>
            <Grid.Column>
              <div className="metric">
                <h2 className="metric-value">25+</h2>
                <p className="metric-label">Industries</p>
              </div>
            </Grid.Column>
            <Grid.Column>
              <div className="metric">
                <h2 className="metric-value">$2B+</h2>
                <p className="metric-label">Transactions</p>
              </div>
            </Grid.Column>
            <Grid.Column>
              <div className="metric">
                <h2 className="metric-value">98%</h2>
                <p className="metric-label">Satisfaction</p>
              </div>
            </Grid.Column>
          </Grid>
        </Container>
      </Segment>

      {/* Case Studies Preview */}
      <Segment className="case-studies" vertical>
        <Container>
          <Header as='h2' textAlign='center' style={{ marginBottom: '2em' }}>
            Industry Success Stories
          </Header>
          
          <Grid columns={3} stackable>
            <Grid.Column>
              <Card>
                <Card.Content>
                  <Card.Header>
                    <Icon name='building outline' />
                    Construction Company
                  </Card.Header>
                  <Card.Meta>50% Efficiency Gain</Card.Meta>
                  <Card.Description>
                    Major construction firm reduced procurement time by 50% 
                    and saved 15% on material costs using Trading Systems.
                  </Card.Description>
                </Card.Content>
                <Card.Content extra>
                  <Button basic fluid>Read Case Study</Button>
                </Card.Content>
              </Card>
            </Grid.Column>
            <Grid.Column>
              <Card>
                <Card.Content>
                  <Card.Header>
                    <Icon name='factory' />
                    Manufacturing Plant
                  </Card.Header>
                  <Card.Meta>30% Cost Reduction</Card.Meta>
                  <Card.Description>
                    Manufacturer streamlined supply chain operations and reduced 
                    inventory costs by 30% with our platform.
                  </Card.Description>
                </Card.Content>
                <Card.Content extra>
                  <Button basic fluid>Read Case Study</Button>
                </Card.Content>
              </Card>
            </Grid.Column>
            <Grid.Column>
              <Card>
                <Card.Content>
                  <Card.Header>
                    <Icon name='tree' />
                    Lumber Distributor
                  </Card.Header>
                  <Card.Meta>2x Growth</Card.Meta>
                  <Card.Description>
                    Regional lumber distributor doubled their customer base 
                    within 12 months using Trading Systems marketplace.
                  </Card.Description>
                </Card.Content>
                <Card.Content extra>
                  <Button basic fluid>Read Case Study</Button>
                </Card.Content>
              </Card>
            </Grid.Column>
          </Grid>
        </Container>
      </Segment>

      {/* CTA Section */}
      <Segment className="industries-cta" inverted vertical>
        <Container textAlign='center'>
          <Header as='h2' inverted>
            Find Your Industry Solution
          </Header>
          <p style={{ fontSize: '1.1em', marginBottom: '2em' }}>
            Discover how Trading Systems can transform your industry-specific 
            challenges into competitive advantages.
          </p>
          <Button size='large' inverted as={Link} to='/contact-us'>
            Get Industry Demo
          </Button>
          <Button size='large' inverted basic as={Link} to='/resources'>
            Industry Resources
          </Button>
        </Container>
      </Segment>
    </div>
  )
}

export default Industries